
import { useEffect, useRef, useState } from "react";
import { Menu, Loader2, Download } from "lucide-react";
import { gsap } from "gsap";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

interface MobileNavbarProps {
  title?: string;
  loading?: boolean;
  onMenuClick: () => void;
  showInstallButton?: boolean;
  onInstallClick?: () => void;
}

export function MobileNavbar({ 
  title = "Solucionar Soldas", 
  loading = false,
  onMenuClick,
  showInstallButton = false,
  onInstallClick
}: MobileNavbarProps) {
  const navbarRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { toast } = useToast();

  // Animation when component mounts
  useEffect(() => {
    if (!navbarRef.current) return;
    
    gsap.fromTo(
      navbarRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" }
    );
  }, []);

  // Hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar if scrolling up or at top
      if (currentScrollY <= 10 || currentScrollY < lastScrollY) {
        setIsVisible(true);
      } 
      // Hide navbar if scrolling down more than 50px
      else if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Animation for showing/hiding navbar
  useEffect(() => {
    if (!navbarRef.current) return;
    
    gsap.to(navbarRef.current, {
      y: isVisible ? 0 : -100,
      opacity: isVisible ? 1 : 0,
      duration: 0.3,
      ease: "power3.out"
    });
  }, [isVisible]);

  return (
    <div 
      ref={navbarRef}
      className="fixed top-0 left-0 right-0 z-40 bg-[#1A1F2C]/95 backdrop-blur-md shadow-md"
    >
      <div className="flex justify-between items-center h-16 px-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onMenuClick}
          className="text-white hover:bg-white/10"
        >
          <Menu className="h-6 w-6" />
        </Button>
        
        <h1 className="text-lg font-['Russo_One'] text-white truncate">
          {title}
        </h1>
        
        <div className="flex items-center gap-2">
          {loading && (
            <div className="h-5 w-5 rounded-full border-2 border-t-transparent border-[#0073B7] animate-spin"></div>
          )}
          
          {showInstallButton && onInstallClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={onInstallClick}
              className="text-xs border-[#0073B7] text-[#0073B7]"
            >
              <Download className="h-4 w-4 mr-1" />
              Instalar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
